import type {
  PropertyDetailResponseDto,
  PropertyDetailWireData,
  PropertyDetailWireFinancials,
} from './propertyDetailContracts'
import type { PropertyDetail, PropertyFinancials } from '../types/propertyDetailTypes'
import type { AppError } from '@core/api/normalizeError'

// The detail endpoint wraps its payload in `{ success, data, message? }`.
// Unwrap, normalise nulls/defaults, and pass through as the camelCase view
// model. Surface "success: false" as an AppError-shaped throw so TanStack
// Query publishes it via `error` with the same plain-object contract every
// container reads via `error.value as AppError`.
export function parsePropertyDetail(resp: PropertyDetailResponseDto): PropertyDetail {
  if (!resp || typeof resp !== 'object' || !('data' in resp)) {
    const error: AppError = { message: 'Invalid property detail response.' }
    throw error
  }
  if (resp.success === false || !resp.data) {
    const error: AppError = { message: resp.message ?? 'Failed to load property detail.' }
    throw error
  }
  return toViewModel(resp.data)
}

const toFinancials = (wire: PropertyDetailWireFinancials | null): PropertyFinancials => ({
  currentMarketValue: wire?.currentMarketValue ?? null,
  grossYieldPercentage: wire?.grossYieldPercentage ?? null,
  netYieldPercentage: wire?.netYieldPercentage ?? null,
  annualRentalIncome: wire?.annualRentalIncome ?? null,
  annualServiceChargeTotal: wire?.annualServiceChargeTotal ?? null,
  propertyManagementFeePct: wire?.propertyManagementFeePct ?? null,
  insuranceCostAnnual: wire?.insuranceCostAnnual ?? null,
  acquisitionFee: wire?.acquisitionFee ?? null,
  dldFee: wire?.dldFee ?? null,
  tokenisationFee: wire?.tokenisationFee ?? null,
  exitFee: wire?.exitFee ?? null,
  platformFeePercentage: wire?.platformFeePercentage ?? null,
  platformFeeAmount: wire?.platformFeeAmount ?? null,
  exitFeePercentage: wire?.exitFeePercentage ?? null,
})

const toViewModel = (wire: PropertyDetailWireData): PropertyDetail => ({
  offeringId: wire.offeringId,
  propertyId: wire.propertyId,
  offeringTitle: wire.offeringTitle,
  offeringDescription: wire.offeringDescription,
  offeringStatus: wire.offeringStatus,
  listingPrice: wire.listingPrice,
  totalTokens: wire.totalTokens,
  tokensSold: wire.tokensSold,
  tokensAvailable: wire.tokensAvailable,
  tokenPrice: wire.tokenPrice,
  minInvestmentAmount: wire.minInvestmentAmount,
  maxInvestmentAmount: wire.maxInvestmentAmount,
  totalRaised: wire.totalRaised,
  totalInvestors: wire.totalInvestors,
  investmentWindowStart: wire.investmentWindowStart ?? '',
  investmentWindowEnd: wire.investmentWindowEnd ?? '',
  targetRoi5yr: wire.targetRoi5yr,
  expectedRentalYield: wire.expectedRentalYield,
  expectedCapitalAppreciation: wire.expectedCapitalAppreciation,
  rentalDistributionFrequency: wire.rentalDistributionFrequency,
  term: wire.term,
  propertyName: wire.propertyName,
  propertyType: wire.propertyType,
  propertyStatus: wire.propertyStatus,
  propertyDescription: wire.propertyDescription,
  addressLine1: wire.addressLine1,
  addressLine2: wire.addressLine2,
  city: wire.city,
  state: wire.state,
  builtUpAreaSqm: wire.builtUpAreaSqm,
  actualAreaSqm: wire.actualAreaSqm ?? null,
  numberOfBedrooms: wire.numberOfBedrooms,
  numberOfBathrooms: wire.numberOfBathrooms,
  parkingSpaces: wire.parkingSpaces,
  yearBuilt: wire.yearBuilt,
  isFurnished: wire.isFurnished ?? false,
  developerName: wire.developerName,
  areaName: wire.areaName,
  buildingName: wire.buildingName,
  latitude: wire.latitude,
  longitude: wire.longitude,
  googleMapsUrl: wire.googleMapsUrl,
  fundedPercentage: wire.fundedPercentage,
  availableAmount: wire.availableAmount,
  daysToClose: wire.daysToClose,
  daysToLaunch: wire.daysToLaunch,
  isBookmarked: wire.isBookmarked,
  financials: toFinancials(wire.financials),
  media: (wire.media ?? []).map((media) => ({
    id: media.id,
    filePath: media.filePath,
    mediaType: media.mediaType,
    isCover: media.isCover,
    isFeatured: media.isFeatured,
    displayOrder: media.displayOrder,
    categoryName: media.categoryName,
  })),
  documents: (wire.documents ?? []).map((doc) => ({
    id: doc.id,
    documentType: doc.documentType,
    documentTitle: doc.documentTitle,
    filePath: doc.filePath,
    fileName: doc.fileName,
    mimeType: doc.mimeType,
  })),
  highlights: (wire.highlights ?? []).map((highlight) => ({
    id: highlight.id,
    highlightText: highlight.highlightText,
    highlightBrief: highlight.highlightBrief,
    highlightIcon: highlight.highlightIcon,
    displayOrder: highlight.displayOrder,
  })),
  amenities: (wire.amenities ?? []).map((amenity) => ({
    id: amenity.id,
    name: amenity.name,
    icon: amenity.icon,
    isCustom: amenity.isCustom,
    groupName: amenity.groupName,
    notes: amenity.notes,
  })),
})
